import { useResizeObserver } from '@react-aria/utils';
import { useCallback, useState, ReactNode } from 'react';
import {
  ListBox,
  ListBoxItem,
  Popover,
  PopoverProps,
  Selection,
  Key
} from 'react-aria-components';

import { useDialogContext } from '@/fo/ui/kit/drawer/base';
import type { CatalogTheme } from '@/fo/util/theme';

import { styles } from './UiKitListboxBase.styles';

export interface BaseOption {
  label: string;
  value: string;
}

export type Option<T = object> = BaseOption & T;

export interface UiKitListboxBaseProps<T extends Option> extends PopoverProps {
  options: T[];
  renderOption?: (option: T) => ReactNode;
  selectionMode?: 'single' | 'multiple';
  selectedKeys?: Iterable<Key>;
  onSelectionChange?: (keys: Selection) => void;
  disabledKeys?: Iterable<Key>;
  isOpen?: boolean;
  isNonModal?: boolean;
  renderListBox?: (options: T[], optionClassName: string) => ReactNode;
  width?: string | null;
  value?: string;
  className?: string;
  classNameOption?: string;
  theme?: CatalogTheme;
}

export function UiKitListboxBase<T extends Option>({
  options,
  triggerRef,
  renderOption,
  selectionMode = 'single',
  selectedKeys,
  onSelectionChange,
  disabledKeys,
  isOpen,
  renderListBox,
  width = null,
  classNameOption,
  isNonModal = false,
  value,
  className,
  theme = 'default',
  ...props
}: UiKitListboxBaseProps<T>) {
  const slots = styles({ isEmpty: options.length === 0, theme });
  const { contentRef: contentDialogRef } = useDialogContext();

  const [menuWidth, setMenuWidth] = useState<string | null>(width);

  const onResize = useCallback(() => {
    if (width) {
      setMenuWidth(width);
      return;
    }

    const calculateMenuWidth = () => {
      if (triggerRef?.current) {
        const triggerRect = triggerRef.current.getBoundingClientRect();
        setMenuWidth(`${triggerRect.width}px`);
      }
    };

    calculateMenuWidth();

    if (contentDialogRef) {
      setTimeout(() => {
        calculateMenuWidth();
      }, 100);
    }
  }, [triggerRef, contentDialogRef, setMenuWidth, width]);

  useResizeObserver({
    ref: triggerRef,
    onResize
  });

  return (
    <Popover
      triggerRef={triggerRef}
      isOpen={isOpen}
      className={slots.base({ className })}
      UNSTABLE_portalContainer={contentDialogRef ?? undefined}
      offset={1}
      style={{ '--menu-width': menuWidth } as React.CSSProperties}
      isNonModal={isNonModal}
      {...props}
    >
      {renderListBox ? (
        renderListBox(options, slots.option())
      ) : (
        <ListBox
          className={slots.listbox()}
          items={options}
          selectionMode={selectionMode}
          selectedKeys={selectedKeys}
          onSelectionChange={onSelectionChange}
          disabledKeys={disabledKeys}
        >
          {(item) => (
            <ListBoxItem
              key={item.value}
              id={item.value}
              className={slots.option({
                hide: value === item.value,
                className: classNameOption
              })}
              textValue={item.label}
            >
              {renderOption ? renderOption(item) : item.label}
            </ListBoxItem>
          )}
        </ListBox>
      )}
    </Popover>
  );
}
